import React from "react";
import { useAuth } from "../../hooks/useAuth";

/**
 * 权限控制组件
 * 根据用户是否有指定的权限来控制子组件的渲染
 *
 * @param {Object} props
 * @param {string|Array} props.permission - 需要的权限标识符，可以是单个权限或权限数组
 * @param {boolean} props.requireAll - 当为true时，需要用户拥有所有指定的权限；当为false时，只需要有其中之一
 * @param {React.ReactNode} props.children - 要渲染的子组件
 * @param {React.ReactNode} props.fallback - 当用户没有权限时显示的组件，默认为null
 * @returns {React.ReactNode}
 */
const PermissionGuard = ({ permission, requireAll = true, children, fallback = null }) => {
    const { user, isAuthenticated, loading } = useAuth();

    // 如果用户信息还在加载中，返回null避免闪烁
    if (loading) {
        return null;
    }

    // 如果用户未登录，不显示任何内容
    if (!isAuthenticated || !user) {
        return fallback;
    }

    // 如果用户是超级管理员，显示所有内容
    if (user.is_superuser) {
        return children;
    }

    // 获取用户权限
    const userPermissions = user.permissions || [];

    // 权限检查逻辑
    const hasPermission = () => {
        // 如果没有指定权限，默认允许访问
        if (!permission) return true;

        // 将单个权限转换为数组
        const requiredPermissions = Array.isArray(permission) ? permission : [permission];

        // 如果需要的权限为空数组，默认允许访问
        if (requiredPermissions.length === 0) return true;

        // 根据requireAll属性决定权限检查策略
        if (requireAll) {
            // 用户必须拥有所有指定的权限
            return requiredPermissions.every((perm) => userPermissions.includes(perm));
        } else {
            // 用户只需拥有其中一个权限即可
            return requiredPermissions.some((perm) => userPermissions.includes(perm));
        }
    };

    // 根据权限检查结果决定是否渲染子组件
    return hasPermission() ? children : fallback;
};

export default PermissionGuard;
